import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ArrowLeft, Award, AlertTriangle, ShieldAlert, FileDown, BadgeCheck, Sparkles, Send, FileSpreadsheet, FileArchive } from "lucide-react";
import { toast } from "sonner";
import { PILLAR, BAND_COLORS, BAND_LABEL, assignBand } from "@/lib/esg";
import { SectorPositioning } from "@/components/results/SectorPositioning";
import WorkflowStepper from "@/components/WorkflowStepper";
import AssessmentTeamPanel from "@/components/AssessmentTeamPanel";
import AssessmentTestToggle from "@/components/AssessmentTestToggle";
import AssessmentTypeField from "@/components/AssessmentTypeField";
import SnapshotBanner from "@/components/SnapshotBanner";
import { hasRole } from "@/lib/roles";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const canEdit = hasRole(user, "valuatore");

  async function load() {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([api.get(`/assessments/${id}/results`), api.get(`/assessments/${id}`)]);
      setData(r.data);
      setAssessment(a.data);
    } finally { setLoading(false); }
  }
  const loadCb = useCallback(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadCb(); }, [loadCb]);

  async function updateAssessment(patch) {
    try {
      await api.patch(`/assessments/${id}`, patch);
      await loadCb();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingCert, setGeneratingCert] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiPlanAt, setAiPlanAt] = useState(null);
  const [aiPlanGenerated, setAiPlanGenerated] = useState(false);
  const aiPlanRef = useRef(null);

  const loadAiPlan = useCallback(async () => {
    try {
      const { data } = await api.get(`/assessments/${id}/action-plan`);
      setAiPlan(data.action_plan || []);
      setAiPlanAt(data.generated_at || null);
    } catch (e) {
      // 404 means assessment missing → main load() will handle
    }
  }, [id]);
  useEffect(() => { loadAiPlan(); }, [loadAiPlan]);

  async function generateAiActionPlan() {
    setGeneratingAi(true);
    try {
      const { data: res } = await api.post(`/assessments/${id}/action-plan/generate`);
      if (res.generated === 0) {
        toast.info("Tutti i topic hanno score ≥ 50 — nessuna raccomandazione AI necessaria.");
      } else {
        toast.success(
          `Piano d'azione AI generato per ${res.generated} topic` +
          (res.from_cache_count ? ` (${res.from_cache_count} da cache)` : "")
        );
      }
      await loadAiPlan();
      setAiPlanGenerated(true);
      // Scroll to AI plan panel after a tick so DOM is updated
      setTimeout(() => {
        aiPlanRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione AI");
    } finally { setGeneratingAi(false); }
  }

  async function _downloadPdf(path, setBusy, fallbackName) {
    setBusy(true);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = res.data;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : fallbackName;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`${fallbackName.includes("Cert") ? "Certificato" : "Report"} PDF scaricato`);
    } catch (e) {
      toast.error("Errore nella generazione del PDF");
    } finally { setBusy(false); }
  }

  const generateReport = () => _downloadPdf(`/assessments/${id}/report`, setGeneratingPdf, `viva-esg-report-${id}.pdf`);
  const generateCertificate = () => _downloadPdf(`/assessments/${id}/certificate`, setGeneratingCert, `viva-esg-Certificato-${id}.pdf`);

  const [generatingXlsx, setGeneratingXlsx] = useState(false);
  const [generatingZip, setGeneratingZip] = useState(false);

  async function downloadEvidenceZip() {
    setGeneratingZip(true);
    try {
      const res = await api.get(`/assessments/${id}/evidence/zip`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `viva-esg-evidenze-${id}.zip`;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Evidenze scaricate (ZIP)");
    } catch (e) {
      toast.error("Errore nel download dell'archivio evidenze");
    } finally { setGeneratingZip(false); }
  }
  async function exportXlsx() {
    setGeneratingXlsx(true);
    try {
      const res = await api.get(`/assessments/${id}/export-xlsx`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `viva-esg-export-${id}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Excel scaricato");
    } catch (e) {
      toast.error("Errore nella generazione del file Excel");
    } finally { setGeneratingXlsx(false); }
  }

  const [publishingRepo, setPublishingRepo] = useState(false);
  async function publishToRepository() {
    if (!confirm("Pubblicare Report e Certificato nel repository del cliente?\n\nVerrà inviata anche un'email di notifica a tutti gli utenti cliente di questa azienda.")) return;
    setPublishingRepo(true);
    try {
      const { data: r } = await api.post(`/assessments/${id}/publish-to-repository`);
      toast.success(`Deliverable pubblicati nel repository · ${r.emails_sent || 0} email inviate`);
    } catch (e) {
      toast.error("Pubblicazione fallita");
    } finally { setPublishingRepo(false); }
  }

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>;
  if (!data) return <div className="p-8">Risultati non disponibili.</div>;

  const fmt = (v) => v === null || v === undefined ? "—" : v.toFixed(1);
  const band = data.band;
  const bandColor = band ? BAND_COLORS[band] : "#94A3B8";

  const radarData = ["E", "S", "G"].map((p) => ({
    pillar: PILLAR[p].label,
    score: p === "E" ? data.score_e : p === "S" ? data.score_s : data.score_g,
    full: 100,
  }));

  const topicChartData = data.topics.map((t) => ({
    name: t.topic_code,
    label: t.label,
    score: t.score === null ? 0 : Math.round(t.score),
    pillar: t.pillar,
  }));

  // Compute raw rating and active cap flags (for warning banners)
  const allPillarsAvailable = data.score_e !== null && data.score_s !== null && data.score_g !== null;
  const rawRating = allPillarsAvailable
    ? data.score_e * data.weights.E + data.score_s * data.weights.S + data.score_g * data.weights.G
    : null;
  const minPillar = allPillarsAvailable ? Math.min(data.score_e, data.score_s, data.score_g) : null;
  const capV1Active = !!data.has_major_nc;
  const capV4Active = !capV1Active && allPillarsAvailable && minPillar < 30;
  const capV7Active = !capV1Active && !capV4Active && rawRating !== null && rawRating >= 90 && !data.has_external_assurance;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <button
            onClick={() => navigate("/app")}
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 hover:text-slate-700"
            data-testid="back-button"
          >
            <ArrowLeft className="w-3 h-3" /> Torna alla dashboard
          </button>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{assessment?.organization?.ragione_sociale}</h1>
          <p className="text-slate-500 mt-1">Profilo {data.profile_level} · Cluster {data.cluster_code} · {assessment?.year}</p>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => navigate(`/assessment/${id}/compila`)} variant="outline" data-testid="edit-answers-button">
              Modifica risposte
            </Button>
            <Button onClick={() => navigate(`/assessment/${id}/materialita`)} variant="outline" data-testid="open-materiality-button">
              Doppia materialità
            </Button>
            <Button onClick={() => navigate(`/assessment/${id}/disclosure`)} variant="outline" data-testid="open-disclosure-button">
              Disclosure VSME/ESRS
            </Button>
            <Button
              onClick={generateAiActionPlan}
              disabled={generatingAi}
              data-testid="generate-ai-plan-button"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              title="Genera raccomandazioni operative AI per i topic con score < 50"
            >
              {generatingAi ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Piano AI
            </Button>
            <Button
              onClick={generateCertificate}
              disabled={generatingCert || data.final_rating === null}
              data-testid="generate-certificate-button"
              variant="outline"
              className="border-viva-200 text-viva-700 hover:bg-viva-50"
            >
              {generatingCert ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BadgeCheck className="w-4 h-4 mr-2" />}
              Scarica Certificato
            </Button>
            <Button
              onClick={exportXlsx}
              disabled={generatingXlsx}
              data-testid="export-xlsx-button"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              title="Esporta i dati completi dell'assessment in Excel"
            >
              {generatingXlsx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Esporta Excel
            </Button>
            {canEdit && (
              <Button
                onClick={downloadEvidenceZip}
                disabled={generatingZip}
                data-testid="download-evidence-zip-button"
                variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-50"
                title="Scarica tutte le evidenze caricate in un unico archivio ZIP (admin/valutatore)"
              >
                {generatingZip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileArchive className="w-4 h-4 mr-2" />}
                Evidenze (ZIP)
              </Button>
            )}
            <Button
              onClick={publishToRepository}
              disabled={publishingRepo || data.final_rating === null}
              data-testid="publish-to-repository-button"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              title="Genera Report+Certificato e li pubblica nel repository del cliente con notifica email"
            >
              {publishingRepo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Pubblica al cliente
            </Button>
            <Button
              onClick={generateReport}
              disabled={generatingPdf}
              data-testid="generate-report-button"
              className="bg-viva-500 hover:bg-viva-600 text-white"
            >
              {generatingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Genera Rapporto
            </Button>
          </div>
        )}
      </div>

      {/* Workflow stepper (Disciplinare § 6.5) */}
      <div className="mb-6">
        <div className="flex flex-wrap justify-end gap-2">
          <AssessmentTypeField assessmentId={id} isAdmin={hasRole(user, "admin")} />
          {hasRole(user, "admin") && <AssessmentTestToggle assessmentId={id} />}
        </div>
        <SnapshotBanner results={data} assessmentId={id} isAdmin={hasRole(user, "admin")} onFrozen={load} />
        <AssessmentTeamPanel assessmentId={id} />
        <WorkflowStepper assessmentId={id} />
      </div>

      {/* Cap warning banners */}
      {(capV1Active || capV4Active || capV7Active) && (
        <div className="space-y-3 mb-6" data-testid="cap-banners">
          {capV1Active && (
            <div data-testid="banner-cap-v1" className="flex items-start gap-3 p-4 rounded-2xl border border-red-200 bg-red-50">
              <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <div className="font-semibold text-red-900">Floor V1 attivo — Non conformità maggiore</div>
                <div className="text-sm text-red-700">Il rating finale è forzato sotto 35 (banda V1 "Critico") indipendentemente dai punteggi calcolati.</div>
              </div>
            </div>
          )}
          {capV4Active && (
            <div data-testid="banner-cap-v4" className="flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-amber-50">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <div className="font-semibold text-amber-900">Cap V4 attivo — Pilastro sotto 30</div>
                <div className="text-sm text-amber-800">
                  Almeno un pilastro è sotto 30 (minimo: {minPillar.toFixed(1)}). Il rating finale è limitato a 69.9 (banda V4 "Adeguato" max).
                </div>
              </div>
            </div>
          )}
          {capV7Active && (
            <div data-testid="banner-cap-v7" className="flex items-start gap-3 p-4 rounded-2xl border border-indigo-200 bg-indigo-50">
              <AlertTriangle className="w-5 h-5 text-indigo-700 mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <div className="font-semibold text-indigo-900">Cap V7 attivo — Manca assurance esterna</div>
                <div className="text-sm text-indigo-800">
                  Rating grezzo {rawRating.toFixed(1)} ≥ 90 ma senza assurance esterna sulla rendicontazione. Il rating finale è limitato a 89.9 (banda V6 max). Spunta "Assurance esterna" qui sotto per ottenere V7.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bento grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Hero rating */}
        <Card data-testid="hero-rating-card" className="lg:col-span-5 p-8 rounded-2xl shadow-soft border-slate-100 relative overflow-hidden">
          <div className="absolute inset-0 viva-gradient opacity-60 pointer-events-none" />
          <div className="relative">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Rating Finale</div>
            <div className="flex items-end gap-4">
              <div className="font-heading text-7xl font-bold text-slate-900" data-testid="final-rating">
                {fmt(data.final_rating)}
              </div>
              {band && (
                <div
                  data-testid="viva-band-badge"
                  className="px-4 py-2 rounded-xl text-white font-heading font-bold text-2xl"
                  style={{ backgroundColor: bandColor }}
                >
                  {band}
                </div>
              )}
            </div>
            <div className="mt-3 text-slate-600">
              {band ? (
                <span className="font-medium">
                  Banda VIVA: <span className="font-heading font-semibold" style={{ color: bandColor }}>{band} · {BAND_LABEL[band]}</span>
                </span>
              ) : "Compila tutte le domande per ottenere il rating."}
            </div>

            {data.has_major_nc && (
              <div className="mt-4 text-xs font-semibold text-white inline-block px-3 py-1.5 rounded-full" style={{ backgroundColor: BAND_COLORS.V1 }}>
                ⚠ NC grave attiva — Floor V1 "Critico"
              </div>
            )}

            {/* Band scale */}
            <div className="mt-6 grid grid-cols-7 gap-1.5">
              {["V1","V2","V3","V4","V5","V6","V7"].map((b) => (
                <div
                  key={b}
                  className={`h-2.5 rounded-full ${band === b ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                  style={{ backgroundColor: BAND_COLORS[b], opacity: band === b ? 1 : 0.35 }}
                />
              ))}
            </div>
          </div>
        </Card>

        {/* Pillar scores */}
        {["E", "S", "G"].map((p) => {
          const meta = PILLAR[p];
          const Icon = meta.icon;
          const v = p === "E" ? data.score_e : p === "S" ? data.score_s : data.score_g;
          const w = data.weights[p];
          const pBand = assignBand(v);
          const pBandColor = pBand ? BAND_COLORS[pBand] : "#94A3B8";
          return (
            <Card
              key={p}
              data-testid={`pillar-score-${p}`}
              className="lg:col-span-7 lg:col-span-[unset] xl:col-span-[unset] p-6 rounded-2xl shadow-soft border-slate-100"
              style={{ gridColumn: "span 7 / span 7" }}
            >
              <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${meta.bg} ${meta.text} text-xs font-semibold uppercase tracking-wider`}>
                  <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                  {meta.label} · Peso {(w * 100).toFixed(0)}%
                </div>
                {pBand && (
                  <span
                    data-testid={`pillar-band-${p}`}
                    className="px-2.5 py-1 rounded-full text-white text-xs font-heading font-bold"
                    style={{ backgroundColor: pBandColor }}
                    title={BAND_LABEL[pBand]}
                  >
                    {pBand}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-heading text-5xl font-bold text-slate-900">{fmt(v)}</div>
                <div className="text-slate-400 text-sm">/ 100</div>
              </div>
              {pBand && <div className="text-xs text-slate-500 mt-1">{BAND_LABEL[pBand]}</div>}
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${v || 0}%`, backgroundColor: meta.color }}
                />
              </div>
              {canEdit && (
                <div className="mt-4">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Malus pilastro</label>
                  <Select
                    value={String(assessment[`malus_${p.toLowerCase()}`] || 0)}
                    onValueChange={(v) => updateAssessment({ [`malus_${p.toLowerCase()}`]: Number(v) })}
                  >
                    <SelectTrigger data-testid={`malus-select-${p}`} className="h-9 mt-1 rounded-lg text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 — Nessun malus</SelectItem>
                      <SelectItem value="5">-5 — Osservazione minore</SelectItem>
                      <SelectItem value="15">-15 — Non conformità minore</SelectItem>
                      <SelectItem value="30">-30 — Non conformità maggiore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </Card>
          );
        })}

        {/* Radar */}
        <Card data-testid="radar-chart-card" className="lg:col-span-6 p-6 rounded-2xl shadow-soft border-slate-100">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">Bilanciamento E · S · G</h3>
          <p className="text-sm text-slate-500 mb-4">Distribuzione del punteggio sui tre pilastri.</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis dataKey="pillar" tick={{ fill: "#475569", fontSize: 12, fontFamily: "Outfit" }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <Radar name="Score" dataKey="score" stroke="#65972A" fill="#65972A" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Topic bar chart */}
        <Card data-testid="topic-chart-card" className="lg:col-span-6 p-6 rounded-2xl shadow-soft border-slate-100">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">Score per Topic</h3>
          <p className="text-sm text-slate-500 mb-4">Risultati per ognuno dei 21 topic ESG.</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={topicChartData} margin={{ left: -20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}
                  formatter={(v) => [`${v}/100`, "Score"]}
                  labelFormatter={(l) => topicChartData.find((d) => d.name === l)?.label || l}
                />
                <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                  {topicChartData.map((d) => (
                    <Cell key={d.name} fill={PILLAR[d.pillar].color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Caps & assurance */}
        <Card data-testid="caps-card" className="lg:col-span-12 p-6 rounded-2xl shadow-soft border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-viva-600" />
            <h3 className="font-heading text-lg font-semibold text-slate-900">Cap & Assurance</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50">
              <Checkbox
                checked={assessment?.has_external_assurance || false}
                disabled={!canEdit}
                onCheckedChange={(v) => updateAssessment({ has_external_assurance: !!v })}
                data-testid="checkbox-assurance"
                className="mt-1"
              />
              <div>
                <div className="font-medium text-slate-900">Assurance esterna sulla rendicontazione presente</div>
                <div className="text-sm text-slate-500">Necessaria per ottenere V7 "Leader ESG" (score ≥ 90).</div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50">
              <Checkbox
                checked={assessment?.has_major_nc || false}
                disabled={!canEdit}
                onCheckedChange={(v) => updateAssessment({ has_major_nc: !!v })}
                data-testid="checkbox-nc"
                className="mt-1"
              />
              <div>
                <div className="font-medium text-slate-900">Non conformità grave non sanata</div>
                <div className="text-sm text-slate-500">Forza il rating a V1 "Critico" (max 34.9), indipendentemente dai punteggi.</div>
              </div>
            </label>
          </div>
        </Card>

        {/* Sector positioning (Q indicators benchmarked against cluster P10/P90) */}
        <SectorPositioning topics={data.topics} clusterLabel={data.cluster_label} clusterCode={data.cluster_code} />

        {/* Topic table */}
        <Card data-testid="topic-table-card" className="lg:col-span-12 rounded-2xl shadow-soft border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Dettaglio per Topic</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-6 py-3">Topic</th>
                <th className="px-6 py-3">Pilastro</th>
                <th className="px-6 py-3">Peso</th>
                <th className="px-6 py-3">Compilato</th>
                <th className="px-6 py-3 text-right">Score</th>
                <th className="px-6 py-3 text-right">Banda</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.topics.map((t) => {
                const tBand = assignBand(t.score);
                return (
                  <tr key={t.topic_code}>
                    <td className="px-6 py-3 font-medium text-slate-900">{t.topic_code} — {t.label}</td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PILLAR[t.pillar].bg} ${PILLAR[t.pillar].text}`}>{t.pillar}</span>
                    </td>
                    <td className="px-6 py-3 text-slate-600">{(t.weight * 100).toFixed(0)}%</td>
                    <td className="px-6 py-3 text-slate-600">{t.answered}/{t.total}</td>
                    <td className="px-6 py-3 text-right font-heading font-semibold text-slate-900">{fmt(t.score)}</td>
                    <td className="px-6 py-3 text-right">
                      {tBand ? (
                        <span
                          className="px-2 py-0.5 rounded-full text-white text-xs font-heading font-bold"
                          style={{ backgroundColor: BAND_COLORS[tBand] }}
                        >
                          {tBand}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {/* AI Action Plan panel */}
        {aiPlan && aiPlan.length > 0 && (
          <Card ref={aiPlanRef} className="lg:col-span-12 bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden" data-testid="ai-action-plan">
            <div className="px-6 py-5 flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">Piano d'Azione AI</h3>
                  <p className="text-xs text-slate-500">
                    Raccomandazioni generate da Claude Sonnet 4.6 (Anthropic) · {aiPlan.length} topic processati
                    {aiPlanAt && ` · ${new Date(aiPlanAt).toLocaleString("it-IT")}`}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {aiPlan.map((tp) => (
                <div key={tp.topic_code} data-testid={`ai-topic-${tp.topic_code}`}>
                  <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${PILLAR[tp.pillar]?.bg || "bg-slate-100"}`}>
                    <span className={`font-heading font-bold ${PILLAR[tp.pillar]?.text || "text-slate-900"}`}>
                      {tp.topic_code}
                    </span>
                    <span className="text-sm text-slate-700">{tp.topic_label}</span>
                    <span className="ml-auto text-xs font-semibold text-slate-500">
                      Score: {tp.score?.toFixed(0)}/100
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    {(tp.recommendations || []).map((r, idx) => {
                      const pri = (r.priority || "media").toLowerCase();
                      const priClass =
                        pri === "alta" ? "bg-red-100 text-red-800 border-red-200" :
                        pri === "media" ? "bg-amber-100 text-amber-800 border-amber-200" :
                        "bg-yellow-50 text-yellow-700 border-yellow-200";
                      return (
                        <div key={idx} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40 hover:bg-white transition">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${priClass}`}>
                              {pri}
                            </span>
                            <span className="text-[10px] text-slate-500 font-medium">{r.horizon}</span>
                          </div>
                          <h4 className="font-heading font-semibold text-slate-900 text-sm leading-snug mb-2">{r.title}</h4>
                          <p className="text-xs text-slate-600 leading-relaxed mb-3">{r.description}</p>
                          <div className="text-[11px] text-viva-700 border-t border-slate-200 pt-2">
                            <span className="font-bold text-viva-600">KPI:</span> {r.kpi}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* AI Action Plan — empty state (after generation when no topics < 50) */}
        {aiPlanGenerated && aiPlan && aiPlan.length === 0 && (
          <Card
            ref={aiPlanRef}
            data-testid="ai-action-plan-empty"
            className="lg:col-span-12 bg-white rounded-3xl shadow-sm border border-emerald-200 overflow-hidden"
          >
            <div className="px-6 py-5 flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-white">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">Piano d'Azione AI — nessuna raccomandazione necessaria</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Tutti i topic ESG hanno punteggio ≥ 50. L'AI genera raccomandazioni solo per i topic sotto soglia.
                  Compila più indicatori o attendi che alcuni topic scendano sotto 50 per ottenere un piano operativo.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
